import { useEffect, useReducer } from "react";
import { omit } from "lodash";
import styles from "./__styles__/DateRange.module.scss";
import { Datepicker } from "./Datepicker";
import Radio from "./Radio";

type DateRangeType = {
  startDate: Maybe<Date | string>;
  endDate: Maybe<Date | string>;
};

const sanitizeDateValue = (value: Maybe<Date | string | undefined>) => {
  if (value === "") {
    return null;
  }

  return value ?? null;
};

const DateRange = ({
  onChange,
  value,
  disabled = false,
}: {
  onChange: (value: {
    startDate: Maybe<Date | string>;
    endDate: Maybe<Date | string>;
  }) => void;
  value: DateRangeType;
  disabled?: boolean;
}) => {
  const reducer = (
    state: DateRangeType & {
      rangeType: string;
    },
    action: {
      type: "setRangeType" | "setStartDate" | "setEndDate";
      rangeType?: string;
      startDate?: Date | string;
      endDate?: Date | string;
    },
  ) => {
    switch (action.type) {
      case "setRangeType":
        return {
          rangeType: action.rangeType!,
          startDate: null,
          endDate: null,
        };

      case "setStartDate":
        return { ...state, startDate: sanitizeDateValue(action.startDate) };
      case "setEndDate":
        return {
          ...state,
          endDate: sanitizeDateValue(action.endDate),
        };
      default:
        return {
          rangeType: "all",
          startDate: null,
          endDate: null,
        };
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    ...value,
    rangeType: value.endDate || value.startDate ? "custom" : "all",
  });

  useEffect(() => {
    onChange(omit(state, "rangeType"));
  }, [state]);

  useEffect(() => {
    if (disabled) dispatch({ type: "setRangeType", rangeType: "all" });
  }, [disabled]);

  return (
    <>
      <Radio
        value={state.rangeType}
        onChange={value =>
          dispatch({ type: "setRangeType", rangeType: value! })
        }
        name="dates"
        options={[
          { value: "all", text: "All" },
          { value: "custom", text: "Custom" },
        ]}
        disabled={disabled}
      />

      {state.rangeType === "custom" && (
        <div className={styles["date-range"]}>
          <div className={styles.field}>
            <label className={styles.label}>Between</label>
            <Datepicker
              name="startDate"
              value={state.startDate}
              onChange={value =>
                dispatch({ type: "setStartDate", startDate: value })
              }
              size="smaller"
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>And</label>
            <Datepicker
              name="endDate"
              value={state.endDate}
              onChange={value =>
                dispatch({ type: "setEndDate", endDate: value })
              }
              size="smaller"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default DateRange;
